/*
 * helpers.js
 *
 * Pure (or near-pure) helpers used by panels and actions. These read
 * from `state`; mutations live in actions.js.
 */

import { state } from './state.js';

/**
 * Aantal maanden in de betaalperiode van een item. Voorkeur: `freqM`.
 * Legacy fallback: f='m' → 1, f='j' → 12. Default 1.
 */
export function freqM(item) {
  if (item && typeof item.freqM === 'number' && item.freqM > 0) return item.freqM;
  if (item && item.f === 'j') return 12;
  return 1;
}

/** Maandelijks aandeel van een vast/var item: bedrag / aantal maanden. */
export function ma(item) {
  return item.a / freqM(item);
}

/** Current viewed cycle, formatted as 'YYYY-MM' (zero-padded). De cyclus
 *  wordt benoemd naar de maand waarin de anker-inkomst binnenkomt. */
export function curKey() {
  return state.vY + '-' + String(state.vM).padStart(2, '0');
}

/**
 * Today's cycle, gegeven de anker van een persoon (default: state.person).
 *
 * Gegate door `loonHandled`: zolang de gebruiker "Loon ontvangen?" voor de
 * nieuwe cyclus niet bevestigd heeft, blijven we hangen in de vorige cyclus.
 * Triggert dus pas een doorroll wanneer de gebruiker actief op de melding
 * tikt — voorkomt dat de UI vooruitloopt op een loon dat nog niet binnen is.
 *
 * Gating geldt enkel als de persoon een anker-inkomst heeft EN de anker-dag
 * deze kalendermaand al gepasseerd is. Anders → naturalKey (= pure datum).
 */
export function todayKey(person) {
  const p = person || state.person;
  const ank = ankerDay(p);
  const naturalKey = dateToCycleKey(state.TODAY, ank);
  if (!getAnker(p)) return naturalKey;
  const d = state.S[p];
  if (d?.loonHandled?.[naturalKey]) return naturalKey;
  if (ank > 1 && state.TODAY.getDate() < ank) return naturalKey;
  return prevCycleKey(naturalKey);
}

/** Een cyclus terug ('YYYY-MM' 0-indexed). */
export function prevCycleKey(cKey) {
  const [y, m] = cKey.split('-').map(Number);
  let pm = m - 1, py = y;
  if (pm < 0) { pm = 11; py--; }
  return py + '-' + String(pm).padStart(2, '0');
}

/**
 * Heeft deze persoon nu een open "Loon ontvangen?"-vraag? Returns de
 * naturalKey (= cyclus die wacht op bevestiging) of null. De melding in
 * gatherNotifs is direct gestoeld op deze return-waarde.
 */
export function loonPendingCycleKey(person) {
  const p = person || state.person;
  const anker = getAnker(p);
  if (!anker) return null;
  const ank = ankerDay(p);
  const naturalKey = dateToCycleKey(state.TODAY, ank);
  const d = state.S[p];
  if (d?.loonHandled?.[naturalKey]) return null;
  if (ank > 1 && state.TODAY.getDate() < ank) return null;
  return naturalKey;
}

/** True wanneer de viewed cycle gelijk is aan today's cycle voor de huidige persoon. */
export function isNow() {
  return curKey() === todayKey();
}

// ---------- Anker / cycle helpers ----------

/** De anker-inkomst van een persoon (of null als geen anker). */
export function getAnker(person) {
  return (state.S[person]?.inkomsten || []).find(i => i.anker) || null;
}

/** De startDag van de anker-inkomst. Default 1 als geen anker (= kalendermaand). */
export function ankerDay(person) {
  return getAnker(person)?.startDag || 1;
}

/**
 * Cycle-key voor een gegeven datum. Cycle wordt benoemd naar de
 * uitgeef-maand (waar het meeste spend plaatsvindt), niet naar de
 * maand waarin het loon binnenkomt.
 *
 *  - anchorDay = 1 → cyclus = kalendermaand
 *  - anchorDay = 25, datum 24 mei → cyclus "Mei" (uitgeef-maand)
 *  - anchorDay = 25, datum 26 april → cyclus "Mei" (loon kwam 25 apr, uitgeef-fase tot 24 mei)
 *  - anchorDay = 25, datum 25 mei → cyclus "Juni" (nieuw loon, nieuwe cyclus)
 */
export function dateToCycleKey(date, anchorDay = 1) {
  const y = date.getFullYear();
  const m = date.getMonth();
  if (anchorDay <= 1) return y + '-' + String(m).padStart(2, '0');
  if (date.getDate() >= anchorDay) {
    // Loon kwam vandaag binnen → uitgeef-fase eindigt volgende kalendermaand
    if (m === 11) return (y + 1) + '-00';
    return y + '-' + String(m + 1).padStart(2, '0');
  }
  // Vóór anker-dag → midden in de uitgeef-fase die deze maand eindigt
  return y + '-' + String(m).padStart(2, '0');
}

/**
 * [start, end] data van een cyclus, gegeven cycle-key en anker-startdag.
 * Voor anchorDay > 1: cyclus loopt van (vorige maand anchorDay) tot
 * (deze maand anchorDay - 1). Voor anchorDay = 1: gewoon kalendermaand.
 */
export function cycleRange(cycleKey, anchorDay = 1) {
  const [y, m] = cycleKey.split('-').map(Number);
  if (anchorDay <= 1) {
    const start = new Date(y, m, 1);
    const end   = new Date(y, m + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  }
  const start = new Date(y, m - 1, anchorDay);
  const end   = new Date(y, m,     anchorDay - 1);
  end.setHours(23, 59, 59, 999);
  return [start, end];
}

/** Vast loon = anker-inkomst per maand. /12 als jaarlijks. 0 als geen anker. */
export function vastLoon(person) {
  const a = getAnker(person);
  if (!a) return 0;
  return a.f === 'j' ? a.a / 12 : a.a;
}

/**
 * Aantal "weken" in een cyclus, rekening houdend met de loon-anker-dag.
 * Eerste week loopt vanaf anker tot de eerstvolgende Zondag (1-7 dagen);
 * tussen-weken zijn vol Mon-Sun; laatste week loopt van de laatste Maandag
 * tot het einde van de cyclus. Telt élke geraakte week mee — ook partials.
 */
export function countCycleWeeks(cycleStart, cycleEnd) {
  let weeks = 0;
  const current = new Date(cycleStart);
  current.setHours(0, 0, 0, 0);
  const end = new Date(cycleEnd);
  end.setHours(23, 59, 59, 999);
  while (current <= end) {
    weeks++;
    const day = current.getDay();
    // Sprong naar volgende maandag: 1 dag als zondag, anders (8 - day).
    current.setDate(current.getDate() + (day === 0 ? 1 : (8 - day)));
  }
  return weeks;
}

/**
 * "Deze week"-bereik binnen een cyclus. Standaard Mon-Sun van de week waarin
 * vandaag valt, geknipt aan beide kanten op cycleStart/cycleEnd zodat de
 * eerste week niet vóór de anker-dag begint en de laatste week niet ná
 * de cyclus eindigt. Voor cycli die niet de huidige zijn: clamp vandaag
 * binnen de cyclus zodat de week-tegel ook in verleden-views iets toont.
 */
export function cycleAwareWeekRange(person, cycleKey = curKey()) {
  const ank = ankerDay(person);
  const [cycleStart, cycleEnd] = cycleRange(cycleKey, ank);
  const today = state.TODAY;
  const refRaw = today < cycleStart
    ? new Date(cycleStart)
    : (today > cycleEnd ? new Date(cycleEnd) : new Date(today));
  const ref = new Date(refRaw);
  ref.setHours(0, 0, 0, 0);

  const day = ref.getDay();
  const offset = day === 0 ? 6 : day - 1;
  const mon = new Date(ref);
  mon.setDate(ref.getDate() - offset);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);

  const weekStart = mon < cycleStart ? new Date(cycleStart) : mon;
  const weekEnd   = sun > cycleEnd   ? new Date(cycleEnd)   : sun;
  weekEnd.setHours(23, 59, 59, 999);
  return [weekStart, weekEnd];
}

/**
 * Begrote uitgaven (vast + var monthly share) voor een cyclus — zonder extras.
 * Used als basis voor "nog over deze week": baseMonthly = inkomst - planned.
 * Auto-gezamenlijk telt enkel mee wanneer gem voor deze cyclus actief is.
 */
export function plannedMonthly(person, cycleKey = curKey()) {
  const d = state.S[person] || {};
  const gemActive = d.gemAuto || d.gemHandled?.[cycleKey];
  let total = 0;
  (d.vast || []).forEach(it => {
    if (it.auto && it.cat === 'gezamenlijk' && !gemActive) return;
    total += ma(it);
  });
  (d.var || []).forEach(it => { total += ma(it); });
  // Maandelijkse spaar-bedrag (20% van vast loon, geclamped op het restant)
  // hoort bij de planning — maar wel met respect voor de spaarHandled-keuze:
  // bij "deze maand niet sparen" wordt er 0 afgetrokken zodat het weekbedrag
  // overeenkomstig stijgt; bij een custom bedrag dat custom bedrag.
  total += plannedSpaarFor(person, cycleKey);
  return total;
}

/**
 * Weekly "nog over"-berekening voor de Home-tegel.
 *   baseMonthly       = inkomstenMaand - plannedMonthly  (begroot afgetrokken)
 *   weeks             = countCycleWeeks(cycle)
 *   weeklyShare       = baseMonthly / weeks
 *   extras            = off-plan hist.ex entries (geen vrKey, geen begroot=true)
 *                       in [weekStart, weekEnd]
 *   weeklyRemaining   = weeklyShare - extras
 *
 * Returns alle deelwaardes zodat de UI ze in de sub-line kan tonen.
 */
export function weeklyRemainingFor(person, cycleKey = curKey()) {
  const ank = ankerDay(person);
  const [cycleStart, cycleEnd] = cycleRange(cycleKey, ank);
  const weeks = Math.max(1, countCycleWeeks(cycleStart, cycleEnd));

  const inkomst = inkomstenMaand(person, cycleKey);
  const planned = plannedMonthly(person, cycleKey);
  const baseMonthly = inkomst - planned;
  const weeklyShare = baseMonthly / weeks;

  // Plan per cat: som van ma(item) voor vast + var per categorie.
  // Wordt gebruikt om overshoot-per-cat te berekenen — een entry binnen
  // de begrote pot telt niet als extra, maar het deel dát erover gaat
  // wél (= "te veel uitgegeven aan boodschappen").
  const d = state.S[person] || {};
  const plannedByCat = {};
  (d.vast || []).forEach(it => {
    plannedByCat[it.cat] = (plannedByCat[it.cat] || 0) + ma(it);
  });
  (d.var || []).forEach(it => {
    plannedByCat[it.cat] = (plannedByCat[it.cat] || 0) + ma(it);
  });

  // Reguliere 'sparen' (maandelijkse auto-spaar) wordt al via spaarbedrag in
  // plannedMonthly afgetrokken van baseMonthly — niet dubbel tellen als extra.
  // 'extra_sparen' is een bewuste eenmalige keuze van de gebruiker en hoort
  // wél het week-potje te krimpen ("ik koos om die €50 extra weg te zetten,
  // dus heb ik die deze week niet meer").
  const SAVINGS_CATS = new Set(['sparen']);

  const [weekStart, weekEnd] = cycleAwareWeekRange(person, cycleKey);

  // Groepeer alle relevante hist.ex per cat, gesorteerd op datum, zodat
  // we per cat chronologisch het "potje" kunnen leeg-tellen tot de
  // planned limiet, en daarna alles als overshoot kunnen tellen.
  const byCat = {};
  const h = histFor(person, cycleKey);
  (h.ex || []).forEach(e => {
    if (e.vrKey || e.begroot) return;
    if (SAVINGS_CATS.has(e.cat)) return;
    const dt = parseHistDateLocal(e.date);
    if (!dt) return;
    if (!byCat[e.cat]) byCat[e.cat] = [];
    byCat[e.cat].push({ amt: e.a, dt });
  });

  let extras = 0;
  Object.entries(byCat).forEach(([cat, entries]) => {
    const cap = plannedByCat[cat] || 0;
    // Cap = 0 → off-plan cat: alles deze week telt mee.
    // Cap > 0 → planned: chronologisch optellen, alleen overshoot telt.
    entries.sort((a, b) => a.dt - b.dt);
    let running = 0;
    entries.forEach(e => {
      const before = running;
      running += e.amt;
      const inWeek = e.dt >= weekStart && e.dt <= weekEnd;
      if (!inWeek) return;
      if (cap <= 0) {
        // Off-plan cat: hele bedrag van deze entry telt.
        extras += e.amt;
        return;
      }
      // Planned cat: deel van dit entry-bedrag dat boven `cap` uitkomt.
      let over;
      if (before >= cap)       over = e.amt;                 // alles boven cap
      else if (running <= cap) over = 0;                     // alles nog binnen cap
      else                     over = running - cap;         // gedeeltelijke overshoot
      extras += over;
    });
  });

  return {
    weeklyRemaining: weeklyShare - extras,
    weeklyShare, extras, weeks, baseMonthly,
    weekStart, weekEnd, inkomst, planned
  };
}

/** "D/M/YYYY" → Date, of null. Lokale helper want de bestaande parseHistDate
 *  zit als private function elders in dit bestand. */
function parseHistDateLocal(s) {
  if (!s) return null;
  const [d, m, y] = String(s).split('/').map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

/**
 * Runway: hoeveel maanden kan deze persoon van z'n veiligheidsbuffer leven
 * als z'n inkomen stopt? = buffer / som(ma(vast)).
 * 0 als er geen vaste lasten zijn (delen door 0 → meaningless).
 *
 * Buffer-keuze:
 *  - Mét noodfonds gemarkeerd → enkel noodfonds.gesp (zuiver, want vakantie-
 *    of droomgeld wil je niet aanspreken bij jobverlies).
 *  - Zonder noodfonds → fallback op alle dromen samen, zodat de meter iets
 *    blijft tonen tot de gebruiker er één markeert.
 *
 * Returns object met `months` + `source` ('noodfonds' | 'all-dromen' | 'none')
 * zodat de UI kan tonen welke berekening is gebruikt.
 */
export function runway(person) {
  const d = state.S[person] || {};
  const vastTotal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  if (vastTotal <= 0) return { months: 0, source: 'none', buffer: 0, vastTotal: 0 };
  const nf = (d.dromen || []).find(dr => dr.isNoodfonds);
  if (nf) {
    const buffer = nf.gesp || 0;
    return { months: buffer / vastTotal, source: 'noodfonds', buffer, vastTotal };
  }
  const buffer = (d.dromen || []).reduce((s, dr) => s + (dr.gesp || 0), 0);
  return { months: buffer / vastTotal, source: 'all-dromen', buffer, vastTotal };
}

/**
 * Spaarquote: welk % van het inkomen blijft over deze cyclus na alle
 * uitgaven (vast + var + extras + auto-gem-storting). Voor een afgesloten
 * cyclus = effectief gespaard. Voor de huidige cyclus = projectie op basis
 * van wat tot nu toe is uitgegeven (zal nog dalen).
 *
 * Returns object met `pct` + `inkomst` + `gespaard` zodat de UI sub-tekst
 * kan tonen. `pct` is null als er geen inkomst is (deling door 0).
 *
 * Vuistregel: 20% is gezond, 10% is mager, >25% is sterk.
 */
export function spaarQuote(person, cycleKey = curKey()) {
  const seg = homeSegs(person, cycleKey);
  if (!seg.inkomst || seg.inkomst <= 0) {
    return { pct: null, inkomst: 0, gespaard: 0 };
  }
  const gespaard = Math.max(0, seg.resterend);
  return {
    pct: (gespaard / seg.inkomst) * 100,
    inkomst: seg.inkomst,
    gespaard
  };
}

/**
 * Aantal maanden tot een droom-deadline. Returns null als geen deadline,
 * 0 als de deadline al voorbij is. Telt vanaf vandaag → einde van de
 * doelmaand (afgerond op vol aantal maanden).
 */
export function deadlineMonthsLeft(droom) {
  if (!droom?.deadline) return null;
  const dl = new Date(droom.deadline);
  if (isNaN(dl.getTime())) return null;
  const today = state.TODAY;
  const monthsDiff = (dl.getFullYear() - today.getFullYear()) * 12
                   + (dl.getMonth() - today.getMonth())
                   + (dl.getDate() >= today.getDate() ? 0 : -1);
  return Math.max(0, monthsDiff);
}

/**
 * Maandelijks vereist bedrag om een droom op tijd te halen.
 * Returns null als geen deadline of doel al bereikt.
 */
export function deadlineMaandbedrag(droom) {
  if (!droom?.deadline) return null;
  if ((droom.gesp || 0) >= droom.doel) return 0;
  const remaining = droom.doel - (droom.gesp || 0);
  const months = deadlineMonthsLeft(droom);
  if (months === null) return null;
  if (months === 0) return remaining;  // deadline vandaag/voorbij
  return remaining / months;
}

/**
 * Effectief percentage voor een droom — uit deadline berekend (als ingesteld
 * én een spaarbedrag > 0), anders gewoon de manueel ingestelde droom.pct.
 */
export function effectivePct(droom, person) {
  if (!droom?.deadline) return droom?.pct || 0;
  const need = deadlineMaandbedrag(droom);
  if (need == null) return droom.pct || 0;
  const sp = spaarbedrag(person);
  if (sp <= 0) return droom.pct || 0;
  return Math.min(100, Math.max(0, Math.ceil(need / sp * 100)));
}

/**
 * Gewicht waarmee een droom meedraait in de spaar-verdeling.
 *  - Gepauzeerde droom → 0 (krijgt niets).
 *  - Anders effectivePct(): deadline-berekend of handmatige dr.pct.
 * Wordt overal als vervanger gebruikt voor de oude `dr.pct`-distributie
 * zodat pauze + deadline overal consistent doorwerken.
 */
export function droomWeight(droom, person) {
  if (!droom) return 0;
  if (droom.paused) return 0;
  return effectivePct(droom, person);
}

/**
 * Projectie-datum voor één spaardroom bij huidig spaartempo.
 *  - monthlyShare = bedrag/mnd dat deze droom uit de spaarpot krijgt.
 *  - Returns Date (afgerond naar volledige maanden) of null als doel
 *    al bereikt of share = 0 (geen tempo).
 */
export function doelProjectie(droom, monthlyShare) {
  if (!droom) return null;
  if (droom.gesp >= droom.doel) return null;
  if (!monthlyShare || monthlyShare <= 0) return null;
  const remaining = droom.doel - droom.gesp;
  const monthsLeft = Math.ceil(remaining / monthlyShare);
  const d = new Date(state.TODAY);
  d.setMonth(d.getMonth() + monthsLeft);
  return d;
}

/**
 * Totaal abonnementen-bedrag op jaarbasis voor een persoon. Telt vast+var
 * items met cat='abonn'. Geeft ook count + monthly terug zodat de UI in
 * één oogopslag "5 abonnementen samen: €581/jaar (€48/mnd)" kan tonen.
 */
export function abonnementenJaar(person) {
  const d = state.S[person] || {};
  const items = [...(d.vast || []), ...(d.var || [])].filter(it => it.cat === 'abonn');
  const monthly = items.reduce((s, it) => s + ma(it), 0);
  return { count: items.length, monthly, jaar: monthly * 12 };
}

/**
 * Noodfonds-helpers (Fase B).
 *
 * Eén droom per persoon kan met `isNoodfonds: true` gevlagd worden.
 * Dat is een mijlpaal-droom met eigen voortgangs-tracking en eigen
 * notif als de gespaarde som onder 1 maand vaste lasten zakt.
 *
 * - noodfondsDoelSuggestie = 3 × maandelijkse vaste lasten (klassieke regel)
 * - noodfondsMinimum       = 1 × maandelijkse vaste lasten (alarm-drempel)
 */
export function noodfondsDroom(person) {
  const d = state.S[person] || {};
  return (d.dromen || []).find(dr => dr.isNoodfonds) || null;
}

export function noodfondsDoelSuggestie(person) {
  const d = state.S[person] || {};
  const vastTotal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  return vastTotal * 3;
}

export function noodfondsMinimum(person) {
  const d = state.S[person] || {};
  const vastTotal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  return vastTotal;
}

export function noodfondsStatus(person) {
  const droom = noodfondsDroom(person);
  const doelSugg = noodfondsDoelSuggestie(person);
  const minimum = noodfondsMinimum(person);
  if (!droom) {
    return {
      droom: null, doel: doelSugg, minimum,
      gesp: 0, pct: 0, ratio: 0,
      underMinimum: minimum > 0, reached: false
    };
  }
  const gesp = droom.gesp || 0;
  const doel = droom.doel || doelSugg;
  const ratio = doel > 0 ? gesp / doel : 0;
  return {
    droom, doel, minimum, gesp,
    pct: Math.min(100, Math.round(ratio * 100)),
    ratio,
    underMinimum: minimum > 0 && gesp < minimum,
    reached: gesp >= doel
  };
}

/**
 * Externe-schulden-helpers (Fase B).
 *
 * Externe schulden zijn leningen buiten het huishouden (kredietkaart,
 * lening bij bank, familie buiten het gezin). Apart van `schulden` (= intra-
 * household) want andere flow: rente + min/maand + avalanche-volgorde.
 * Niet relevant op samen — alleen per persoon.
 */
export function externeSchuldenTotaal(person) {
  return (state.S[person]?.externeSchulden || [])
    .reduce((s, x) => s + (x.rest || 0), 0);
}

export function externeSchuldenMaandMin(person) {
  return (state.S[person]?.externeSchulden || [])
    .reduce((s, x) => s + (x.minMaand || 0), 0);
}

export function avalancheVolgorde(person) {
  return [...(state.S[person]?.externeSchulden || [])]
    .filter(x => (x.rest || 0) > 0)
    .sort((a, b) => (b.rente || 0) - (a.rente || 0));
}

/**
 * Onverwacht-trend — over de laatste `n` afgeronde cycli (excl. huidige),
 * som van hist.ex met cat='onverwacht' per cycle. Triggert "is dit nog
 * onverwacht?"-notif wanneer dit structureel gebeurt: élke cyclus had
 * onverwacht-uitgaven, gemiddelde > €50, én er zijn exact n cycli met data.
 */
export function onverwachtTrend(person, n = 3) {
  const tk = todayKey(person);
  const tIdx = cycleIndex(tk);
  const cycles = [];
  for (let i = 1; i <= n; i++) {
    const idx = tIdx - i;
    const y = Math.floor(idx / 12);
    const m = idx % 12;
    if (idx < 0) break;
    const key = y + '-' + String(m).padStart(2, '0');
    const total = (histFor(person, key).ex || [])
      .filter(e => e.cat === 'onverwacht')
      .reduce((s, e) => s + (e.a || 0), 0);
    cycles.push({ key, total });
  }
  const total = cycles.reduce((s, c) => s + c.total, 0);
  const avg = cycles.length > 0 ? total / cycles.length : 0;
  const allHaveEntry = cycles.length === n && cycles.every(c => c.total > 30);
  const allAboveThreshold = allHaveEntry && avg > 50;
  return { cycles, avg, total, allAboveThreshold };
}

/**
 * Inkomsten-ondergrens — minimum inkomstenMaand over de laatste `n`
 * volledig afgeronde cycli (excl. de huidige). Voor cashflow-buffer in
 * Fase C: hoe laag is je inkomen op z'n slechtst geweest?
 * Returns 0 als er geen volledig afgeronde cycli zijn.
 */
export function inkomstenOndergrens(person, n = 6) {
  const tk = todayKey(person);
  const tIdx = cycleIndex(tk);
  let lowest = null;
  for (let i = 1; i <= n; i++) {
    const idx = tIdx - i;
    const y = Math.floor(idx / 12);
    const m = idx % 12;
    if (y < 0 || (y === 0 && m < 0)) break;
    const key = y + '-' + String(m).padStart(2, '0');
    const amt = inkomstenMaand(person, key);
    if (lowest === null || amt < lowest) lowest = amt;
  }
  return lowest === null ? 0 : lowest;
}

/** Excluded cats voor "duurste items"-analyse — niet onderhandelbaar
 *  (Gezamenlijke kosten + Boodschappen + Huisdier, want geen besparen
 *  op levende wezens). */
const NON_BESPAAR_CATS = ['gezamenlijk', 'boodschappen', 'huisdier'];

/**
 * Snel statusoverzicht voor de besparen-flow.
 *  - overVast / overVar: ratio's over de 50/30 targets
 *  - bespaarModus: true als ten minste één bucket over target staat
 */
export function bespaarStatus(person) {
  const loon = vastLoon(person);
  if (loon <= 0) return { bespaarModus: false };
  const d = state.S[person] || {};
  const vastTotal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  const varTotal  = (d.var  || []).reduce((s, it) => s + ma(it), 0);
  const targetVast = loon * 0.5;
  const targetVar  = loon * 0.3;
  const overVast = vastTotal > targetVast;
  const overVar  = varTotal  > targetVar;
  return {
    bespaarModus: overVast || overVar,
    vastTotal, varTotal, targetVast, targetVar,
    overVast, overVar,
    teveelVast: Math.max(0, vastTotal - targetVast),
    teveelVar:  Math.max(0, varTotal  - targetVar)
  };
}

/**
 * Lijst van duurste vast+var items voor de besparen-modal. Gesorteerd op
 * monthly portion descending, excluded cats overgeslagen, top N.
 */
export function expensiveItems(person, limit = 10) {
  const d = state.S[person] || {};
  const all = [
    ...(d.vast || []).map((it, i) => ({ ...it, __type: 'vast', __i: i })),
    ...(d.var  || []).map((it, i) => ({ ...it, __type: 'var',  __i: i }))
  ].filter(it => !NON_BESPAAR_CATS.includes(it.cat) && !it.auto);

  all.sort((a, b) => ma(b) - ma(a));
  return all.slice(0, limit);
}

/**
 * Alle openstaande "naar-samen"-schulden van Sven + Lander, voor de
 * read-only weergave op het Samen-tabblad. Persoon-naam wordt
 * meegenomen zodat de UI weet wie wat schuld heeft.
 */
export function samenSchulden() {
  const out = [];
  ['sven', 'lander'].forEach(p => {
    (state.S[p]?.schulden || []).forEach(s => {
      if (s.kant === 'naar-samen' && s.rest > 0) {
        out.push({ ...s, persoon: p });
      }
    });
  });
  return out;
}

/** Date-string 'D/M/YYYY' → cycle key voor een persoon. */
function schuldCycleKey(person, s) {
  if (s.createdAtCycle) return s.createdAtCycle;
  if (!s.date) return null;
  const [d, m, y] = s.date.split('/').map(Number);
  return dateToCycleKey(new Date(y, m - 1, d), ankerDay(person));
}

/**
 * Hoeveel heeft de gebruiker deze cyclus al van Samen geleend?
 * Triggert een notif als 't te vaak gebeurt (3+ keer of > 15% loon).
 */
export function samenBorrowingStatus(person, cycleKey = curKey()) {
  const list = (state.S[person]?.schulden || [])
    .filter(s => s.kant === 'naar-samen' && schuldCycleKey(person, s) === cycleKey);
  const count = list.length;
  const total = list.reduce((s, x) => s + (x.bedrag || 0), 0);
  const loon = vastLoon(person);
  const threshold = loon * 0.15;
  const tooMuch = count >= 3 || (loon > 0 && total > threshold);
  return { count, total, tooMuch };
}

/**
 * Hoe vaak en hoeveel heeft de gebruiker deze cyclus al uit z'n spaarpot
 * gehaald? Triggert een notif op Home bij "te vaak" (3+ keer of > 15% loon).
 */
export function spaarWithdrawalStatus(person, cycleKey = curKey()) {
  const entries = (histFor(person, cycleKey).in || [])
    .filter(e => e.cat === 'uit_spaar');
  const count = entries.length;
  const total = entries.reduce((s, e) => s + e.a, 0);
  const loon = vastLoon(person);
  const threshold = loon * 0.15;
  const tooMuch = count >= 3 || (loon > 0 && total > threshold);
  return { count, total, tooMuch };
}

/**
 * Spaarbedrag per cyclus, flexibel.
 *
 *  Doel = 20% van vast loon (50/30/20-regel).
 *  Maar als vaste+variabele kosten meer slokken dan 80%, schakelt sparen
 *  terug naar wat er feitelijk over is. Bij volledig over budget → 0.
 *
 *  spaarbedrag = max(0, min(loon × 0.2, loon - vasteTotal - varTotal))
 */
export function spaarbedrag(person) {
  const loon = vastLoon(person);
  if (loon <= 0) return 0;
  const d = state.S[person] || {};
  const vastTotal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  const varTotal  = (d.var  || []).reduce((s, it) => s + ma(it), 0);
  const target    = loon * 0.2;
  const remaining = loon - vastTotal - varTotal;
  return Math.max(0, Math.min(target, remaining));
}

/**
 * Wat de gebruiker EFFECTIEF aan sparen heeft gepland voor een cyclus,
 * gegeven z'n spaarHandled-keuze:
 *   - { kind: 'skip' }           → 0 (deze maand niet sparen)
 *   - { kind: 'custom', amount } → het ingegeven custom bedrag
 *   - anders                     → de auto-berekende spaarbedrag(person)
 *
 * Wordt gebruikt door plannedMonthly zodat het weekbedrag niet onterecht
 * met de auto-spaar wordt belast als de gebruiker zei "deze maand niet".
 */
export function plannedSpaarFor(person, cycleKey = curKey()) {
  const d = state.S[person] || {};
  const handled = d.spaarHandled?.[cycleKey];
  if (handled && typeof handled === 'object') {
    if (handled.kind === 'skip') return 0;
    if (handled.kind === 'custom' && Number.isFinite(handled.amount)) {
      return Math.max(0, handled.amount);
    }
  }
  return spaarbedrag(person);
}

/**
 * Inkomsten die meetellen in een cyclus voor een persoon.
 * Alle vaste inkomsten tellen volledig — geen "progressieve reveal" meer
 * (Lander vond dat verwarrend: Maaltijdcheques pas vanaf 15 juni in pie).
 * Jaarlijkse inkomsten worden door callers als /12 behandeld.
 */
export function inkomstenForCycle(person /* cycleKey, asOf — niet meer gebruikt */) {
  return state.S[person]?.inkomsten || [];
}

/**
 * History bucket for a given person + month key. Lazy-initialises with
 * empty `ex` (uitgaven) and `in` (inkomsten) arrays so callers can push
 * without null-checks. Defaults to current viewed month + current person.
 */
export function histFor(person, key) {
  const d = state.S[person];
  const k = key || curKey();
  if (!d.hist) d.hist = {};
  if (!d.hist[k]) d.hist[k] = { ex: [], in: [] };
  if (!d.hist[k].ex) d.hist[k].ex = [];
  if (!d.hist[k].in) d.hist[k].in = [];
  return d.hist[k];
}

/** Shorthand: current viewed month, current person. */
export function hist() {
  return histFor(state.person);
}

/** History bucket for the *previous* month — used for "verhoog budget?" hints. */
export function prevHist() {
  let m = state.vM - 1;
  let y = state.vY;
  if (m < 0) { m = 11; y--; }
  return histFor(state.person, y + '-' + String(m).padStart(2, '0'));
}

/**
 * Lees de --accent / --accent-samen CSS-vars uit de huidige thema-stack.
 * Zo zijn JS-inline kleuren (KPI-tegelrand, voortgangsbalken, +€-bedragen,
 * ✓-iconen) automatisch theme-aware ipv hardgecodeerd groen/oranje.
 */
export function accentColor() {
  if (typeof document === 'undefined') return '#2a9d5a';
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--accent').trim();
  return v || '#2a9d5a';
}
export function accentSamenColor() {
  if (typeof document === 'undefined') return '#e67e22';
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--accent-samen').trim();
  return v || '#e67e22';
}

/** Accent colour voor de actieve persoon — samen = oranje-equivalent, anders groen-equivalent. */
export function accent() {
  return state.person === 'samen' ? accentSamenColor() : accentColor();
}

// ---------- Kijker-modus helpers ----------

/** De andere persoon (sven ↔ lander). null voor samen. */
export function mateOf(person) {
  if (person === 'sven')   return 'lander';
  if (person === 'lander') return 'sven';
  return null;
}

/** De mate van de huidige user (state.user). */
export function mate() {
  return mateOf(state.user);
}

/**
 * De UI-naam voor een slot-key. Tijdens vroege boot (vóór hydrate, of in
 * seed-mode zonder Supabase) is state.displayNames een fallback met netjes
 * gekapitaliseerde slot-namen ('Sven', 'Lander', 'Samen'). Na hydrate
 * krijgen meKey/mateKey de échte display_name van het profile — zo zien
 * gebruikers met andere namen (bv. "Sarah", "Tim") die ook overal terug.
 */
export function nameFor(personKey) {
  if (!personKey) return '';
  const dn = state.displayNames?.[personKey];
  if (dn) return dn;
  // Fallback: kapitaliseer de slot-key zelf.
  return personKey.charAt(0).toUpperCase() + personKey.slice(1);
}

/**
 * Zit ik in kijker-modus? = ik bekijk de data van mijn mate, niet
 * mijn eigen of Samen. In dat geval is alle write-UI verborgen.
 */
export function isKijker() {
  return state.person !== state.user && state.person !== 'samen';
}

/** Mag de mate van een persoon z'n data inkijken? */
export function mateMayView(person) {
  return !!state.S[person]?.shareWithMate;
}

/**
 * Pride-seizoen: 17 mei (IDAHOT) of een dag in juni (Pride Month).
 * Gebruikt voor zowel de theme-auto-trigger als de rainbow-logo (die los
 * van het thema staat — ook bij Frietjes/Lowie kleurt het logo mee).
 */
export function isPrideSeason(d = new Date()) {
  const month = d.getMonth();  // 0-indexed: mei = 4, juni = 5
  const day   = d.getDate();
  if (month === 5) return true;                  // hele juni
  if (month === 4 && day === 17) return true;    // IDAHOT
  return false;
}

/**
 * Pride opt-out per jaar. localStorage-only — een UI-preference die geen
 * cross-device-sync nodig heeft (zou Sven op zijn gsm anders willen dan
 * op zijn laptop, dan kan dat). Key incl. userId zodat verschillende
 * accounts op één toestel elkaar niet beïnvloeden.
 */
const PRIDE_OPT_OUT_KEY = 'florijn:pride-opt-out';
function prideOptOutStorageKey() {
  return state.userId ? `${PRIDE_OPT_OUT_KEY}:${state.userId}` : PRIDE_OPT_OUT_KEY;
}
export function getPrideOptOutYear() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(prideOptOutStorageKey());
    return v ? parseInt(v, 10) : null;
  } catch { return null; }
}
export function setPrideOptOutYear(year) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(prideOptOutStorageKey(), String(year)); } catch {}
}
export function clearPrideOptOut() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(prideOptOutStorageKey()); } catch {}
}

/** True als auto-Pride NU effect heeft (seizoen + niet uitgevinkt voor dit jaar). */
export function isAutoPrideActive(d = new Date()) {
  if (!isPrideSeason(d)) return false;
  return getPrideOptOutYear() !== d.getFullYear();
}

/**
 * Welk kleur-thema is actief? Geldige settings:
 *   'default' | 'frietjes' | 'lowie' | 'pride'
 * Backwards-compat: oude waardes ('light', 'dark', 'auto') → 'default'.
 * Auto-Pride: staat de gebruiker op 'default' en zitten we in Pride-seizoen
 * zonder opt-out → return 'pride'. Andere thema's blijven gerespecteerd.
 */
export function getActiveTheme() {
  const t = state.S.settings?.theme;
  const saved = ['default', 'frietjes', 'lowie', 'pride'].includes(t) ? t : 'default';
  if (saved === 'default' && isAutoPrideActive()) return 'pride';
  return saved;
}

/**
 * Welke modus is actief: 'light' of 'dark'. Backwards-compat: als de oude
 * theme-string 'dark' was, of mode niet expliciet gezet → infer uit theme.
 */
export function getActiveMode() {
  const m = state.S.settings?.mode;
  if (m === 'light' || m === 'dark') return m;
  // Oude 'theme: dark' → mode dark.
  if (state.S.settings?.theme === 'dark') return 'dark';
  return 'light';
}

/** Past beide axes + de pride-seizoen-flag toe als data-attributen op <html>.
 *  data-pride-season is los van data-theme zodat het logo ook tijdens
 *  juni/IDAHOT als regenboog toont bij Frietjes/Lowie-gebruikers. */
export function applyTheme() {
  document.documentElement.setAttribute('data-theme', getActiveTheme());
  document.documentElement.setAttribute('data-mode',  getActiveMode());
  if (isPrideSeason()) {
    document.documentElement.setAttribute('data-pride-season', 'true');
  } else {
    document.documentElement.removeAttribute('data-pride-season');
  }
}

/**
 * Uitgebreide queer-palette voor pie-slices in pride-modus. Tinten getrokken
 * uit Rainbow Pride + Bi + Pan + Trans + Lesbian + Non-binary + Aro + Ace.
 * Wit/grijs/zwart bewust weggelaten (te weinig contrast met de canvas-bg).
 * Volgorde is zo gekozen dat opeenvolgende kleuren altijd op een andere hue
 * zitten — naast-elkaar-slices in de pie blijven dan onderscheidbaar.
 */
const PRIDE_PALETTE = [
  '#e40303', // Rainbow rood
  '#ffed00', // Rainbow geel
  '#008026', // Rainbow groen
  '#004dff', // Rainbow blauw
  '#750787', // Rainbow paars
  '#ff8c00', // Rainbow oranje
  '#d60270', // Bi pink
  '#5bcefa', // Trans lichtblauw
  '#a30262', // Lesbian dark pink
  '#3da542', // Aro groen
  '#21b1ff', // Pan blauw
  '#ff218c', // Pan pink
  '#fcf434', // Non-binary geel
  '#9b4f96', // Bi paars
  '#ff9b55', // Lesbian oranje
  '#9c59d1'  // Non-binary paars
];

/**
 * Frietjes pie-palette — warme aarde-pastels die matchen met de beige/bruin
 * theme-base. Naast-elkaar-slices hebben merkbaar andere hue zodat de pie
 * leesbaar blijft ondanks dat alles in dezelfde sfeer zit.
 */
const FRIETJES_PALETTE = [
  '#997D61', // theme accent — bruin
  '#D9A887', // warme tan
  '#A3A07A', // soft olive
  '#C9A2A0', // dusty rose
  '#9CAA8E', // sage green
  '#D9C496', // wheat
  '#A89580', // mauve brown
  '#E0B998', // peach beige
  '#7F8C7F', // slate green
  '#B8957A', // caramel
  '#A0A8B5', // dusty slate blue
  '#C97A5E'  // terracotta
];

/**
 * Lowie pie-palette — terracotta + cream pastels rond de Lowie-base.
 */
const LOWIE_PALETTE = [
  '#BF6F41', // theme accent — terracotta
  '#D9965B', // warm oranje
  '#E8B895', // peach
  '#A8A37A', // olive
  '#C9A39A', // dusty rose
  '#7A8C75', // sage
  '#D9A87E', // light peach
  '#8C644D', // deep brown
  '#C97A5E', // coral
  '#B89890', // mauve
  '#D8C9A8', // beige
  '#7A8FAC'  // dusty slate blue (koele tegenpool)
];

/**
 * Vervang de categorie-kleuren door rotatie over een theme-palette wanneer
 * het actieve thema er één heeft (pride / frietjes / lowie). Voor 'default'
 * blijven de originele cat.color-waardes behouden. Muteert niet — geeft een
 * nieuwe array met overschreven `color` terug.
 */
export function themeRecolor(cats) {
  if (!cats?.length) return cats;
  const t = getActiveTheme();
  let palette = null;
  if (t === 'pride')    palette = PRIDE_PALETTE;
  if (t === 'frietjes') palette = FRIETJES_PALETTE;
  if (t === 'lowie')    palette = LOWIE_PALETTE;
  if (!palette) return cats;
  return cats.map((c, i) => ({ ...c, color: palette[i % palette.length] }));
}

/** Backwards-compat alias — oude pride-only naam. */
export const prideRecolor = themeRecolor;

// ---------- Categorie-lookups ----------

/** Vind categorie-object op id. Geeft een fallback-object als de id onbekend is. */
export function catById(id) {
  return (state.S.cats || []).find(c => c.id === id) || {
    id, label: id || 'Onbekend', emoji: '❓', color: '#888', scope: 'uit'
  };
}

/** Alle categorieen voor een scope ('uit' | 'in'). */
export function catsForScope(scope) {
  return (state.S.cats || []).filter(c => c.scope === scope);
}

/**
 * Totaal beschikbare inkomsten in een cycle voor een persoon.
 * Default = viewed cycle. Telt alle vaste inkomsten die in deze cyclus
 * meetellen (met progressive reveal in de huidige cyclus) plus de extra
 * inkomsten uit hist.in.
 */
export function inkomstenMaand(person, cycleKey = curKey()) {
  const list = inkomstenForCycle(person, cycleKey);
  const vast = list.reduce((s, i) => s + (i.f === 'j' ? i.a / 12 : i.a), 0);
  const extra = (histFor(person, cycleKey).in || []).reduce((s, r) => s + (r.a || 0), 0);
  return vast + extra;
}

const RESTEREND_KLEUR = '#d8dde2';

/**
 * Pie-segmenten voor de Home-weergave van een persoon, gegroepeerd per
 * categorie. Telt vast + var + extras (deze maand) bij elkaar op per cat.
 *
 * Geeft ook de totalen terug zodat de KPI-tegel en de legenda dezelfde
 * getallen tonen als de pie.
 */
export function homeSegs(person, cycleKey = curKey()) {
  const d = state.S[person] || {};
  const h = histFor(person, cycleKey);
  const bucket = {};      // catId → bedrag

  const bump = (catId, amt) => {
    if (!amt) return;
    bucket[catId] = (bucket[catId] || 0) + amt;
  };

  // Auto-gezamenlijk verschijnt pas in de pie als de gem-storting voor
  // deze cyclus bevestigd is (of als auto-mode aan staat).
  const gemActive = d.gemAuto || d.gemHandled?.[cycleKey];

  // Voor planned items: monthlyShare = ma(item) = item.a / freqM telt elke
  // cyclus mee als "gereserveerd". Bij betaling alleen overshoot bovenop:
  //  - Monthly (freqM=1): overshoot = spent - monthlyShare (= spent - item.a)
  //  - N-monthly: overshoot = spent - item.a (de hele periode-betaling).
  //    Anders zou de €15/mnd × 12 + €180 betaling dubbel tellen.
  const planContribution = (it, i, type) => {
    const monthlyShare = ma(it);
    const spent  = spentPerItem(person, type, i, cycleKey);
    const cap    = freqM(it) > 1 ? it.a : monthlyShare;
    const over   = Math.max(0, spent - cap);
    return monthlyShare + over;
  };

  (d.vast || []).forEach((it, i) => {
    if (it.auto && it.cat === 'gezamenlijk' && !gemActive) return;
    bump(it.cat, planContribution(it, i, 'vast'));
  });
  (d.var || []).forEach((it, i) => {
    bump(it.cat, planContribution(it, i, 'var'));
  });
  // Off-plan hist.ex entries (zonder vrKey) tellen volledig mee.
  // Begroot-entries (begroot:true met vrKey) zijn al in spentPerItem verwerkt.
  (h.ex || []).forEach(e => {
    if (e.vrKey || e.begroot) return;
    bump(e.cat, e.a);
  });

  const cats = Object.entries(bucket)
    .filter(([, a]) => a > 0)
    .map(([id, a]) => {
      const c = catById(id);
      return { id, label: c.label, emoji: c.emoji, color: c.color, amount: a };
    })
    .sort((x, y) => y.amount - x.amount);

  const uitgegeven = cats.reduce((s, x) => s + x.amount, 0);
  const inkomst    = inkomstenMaand(person, cycleKey);
  const resterend  = inkomst - uitgegeven;

  return { cats, uitgegeven, inkomst, resterend, restColor: RESTEREND_KLEUR };
}

// ---------- Auto-handling van sparen + gem-storting per cyclus ----------

/**
 * Voor elke cyclus die nog niet "afgehandeld" is (sparen of gem):
 *  - Als spaarAuto aan staat → verdeel d.spaar over dromen + push als hist.ex
 *    met cat='sparen' (zodat 't als slice in de pie verschijnt). Mark handled.
 *  - Als gemAuto aan staat → mark handled (geen push; gezamenlijk is al een
 *    auto-vast item dat altijd in de pie zit).
 *
 * Aanroepen bij render() — idempotent dankzij de handled-maps.
 */
export function runAutoActions(person) {
  if (person === 'samen') return;
  const d = state.S[person];
  if (!d) return;

  if (!d.spaarHandled) d.spaarHandled = {};
  if (!d.gemHandled)   d.gemHandled   = {};

  const todayK = todayKey(person);

  if (d.spaarAuto && !d.spaarHandled[todayK]) {
    const amt = spaarbedrag(person);     // 20% van vast loon, auto-volgt
    const tot = (d.dromen || []).reduce((s, dr) => s + droomWeight(dr, person), 0) || 1;
    (d.dromen || []).forEach(dr => {
      const w = droomWeight(dr, person);
      dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
    });
    if (amt > 0) {
      const h = histFor(person, todayK);
      const t = new Date();
      h.ex.push({
        a: amt, cat: 'sparen', desc: 'Maandelijks spaarbedrag (auto)',
        date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear()
      });
    }
    d.spaarHandled[todayK] = true;
  }

  if (d.gemAuto && !d.gemHandled[todayK]) {
    d.gemHandled[todayK] = true;
  }

  // Schuld-termijn-auto-aflos: voor elke schuld met een termijn > 0 die
  // deze cyclus nog niet verwerkt is, trek de termijn af van rest.
  // Voor naar-spaar herstellen we ook dromen.gesp proportioneel + pushEx
  // 'sparen' (klopt met aflossen-flow). Andere kanten: alleen rest aanpassen.
  (d.schulden || []).forEach(s => {
    if (!s.termijn || s.termijn <= 0 || s.rest <= 0) return;
    if (s.lastDeductCycle === todayK) return;
    const amt = Math.min(s.termijn, s.rest);
    s.rest -= amt;
    s.lastDeductCycle = todayK;

    if (s.kant === 'naar-spaar') {
      const tot = (d.dromen || []).reduce((sum, dr) => sum + droomWeight(dr, person), 0) || 1;
      (d.dromen || []).forEach(dr => {
        const w = droomWeight(dr, person);
        dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
      });
      const h = histFor(person, todayK);
      const t = new Date();
      h.ex.push({
        a: amt, cat: 'sparen', desc: 'Termijn-aflos spaarpot (auto)',
        date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear()
      });
    }

    // Sync naar de gelinkte schuld op de andere persoon (voor naar-andere).
    if (s.id && s.andere) {
      const otherList = state.S[s.andere]?.schulden;
      const match = otherList?.find(x => x.id === s.id);
      if (match) {
        match.rest = s.rest;
        match.termijn = s.termijn;
        match.lastDeductCycle = s.lastDeductCycle;
      }
    }
  });
}

// ---------- Auto-items (Gezamenlijke kosten uit Samen) ----------

/**
 * Werkt auto:true vast-items van een persoon bij. Momenteel één type:
 * `cat=gezamenlijk` → bedrag wordt (samen vast+var per maand) / 2.
 *
 * Aanroepen vóór elke render zodat het bedrag altijd vers is wanneer
 * de gebruiker iets in Samen wijzigt.
 */
export function refreshAutoItems(person) {
  if (person === 'samen') return;
  const d = state.S[person];
  if (!d?.vast) return;
  const s = state.S.samen;
  if (!s) return;
  const sTotal = [...(s.vast || []), ...(s.var || [])]
    .reduce((acc, it) => acc + ma(it), 0);
  const aandeel = sTotal / 2;
  d.vast.forEach(item => {
    if (item.auto && item.cat === 'gezamenlijk') {
      item.a = aandeel;
      item.f = 'm';
    }
  });
}

// ---------- Week-budgetten per categorie ----------

/** Weekbudget (€) voor een persoon + cat. 0 als niet ingesteld. */
export function weekBudgetFor(person, catId) {
  return state.S[person]?.weekBudgets?.[catId] || 0;
}

/** Maandbudget = weekbudget × 4 (vuistregel — overschrijf later evt). */
export function monthBudgetFor(person, catId) {
  return weekBudgetFor(person, catId) * 4;
}

/** "D/M/YYYY" → Date object. */
function parseHistDate(s) {
  if (!s) return null;
  const [d, m, y] = s.split('/').map(Number);
  return new Date(y, m - 1, d);
}

/** [maandag, zondag] van de week waarin een datum valt. */
function weekRange(date) {
  const t = new Date(date);
  t.setHours(0, 0, 0, 0);
  const day = t.getDay();
  const offset = day === 0 ? 6 : day - 1;
  const mon = new Date(t);
  mon.setDate(t.getDate() - offset);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return [mon, sun];
}

/** Som van hist.ex deze week voor een persoon + cat. */
export function spentThisWeek(person, catId) {
  const [mon, sun] = weekRange(state.TODAY);
  let total = 0;
  Object.values(state.S[person]?.hist || {}).forEach(h => {
    (h.ex || []).forEach(e => {
      if (e.cat !== catId) return;
      const dt = parseHistDate(e.date);
      if (dt && dt >= mon && dt <= sun) total += e.a;
    });
  });
  return total;
}

/** Som van hist.ex in een cycle voor een persoon + cat. */
export function spentInCycle(person, catId, cycleKey = curKey()) {
  return (histFor(person, cycleKey).ex || [])
    .filter(e => e.cat === catId)
    .reduce((s, e) => s + e.a, 0);
}

/**
 * Som van hist.ex entries die expliciet gelinkt zijn aan een specifiek
 * planned item (via vrKey = '<type>:<index>'). Telt zowel begroot- als
 * extra-entries. Voor het per-item progressie-balkje.
 */
export function spentPerItem(person, type, index, cycleKey = curKey()) {
  const key = `${type}:${index}`;
  return (histFor(person, cycleKey).ex || [])
    .filter(e => e.vrKey === key)
    .reduce((s, e) => s + e.a, 0);
}

/** 'YYYY-MM' → continue maand-index (jaar*12 + maand) voor cycle-diffs. */
function cycleIndex(key) {
  const [y, m] = key.split('-').map(Number);
  return y * 12 + m;
}

/** HTML <input type="month"> waarde 'YYYY-MM' (1-indexed) → cycleKey 'YYYY-MM' (0-indexed). */
export function monthInputToCycleKey(monthStr) {
  if (!monthStr) return null;
  const [y, m] = monthStr.split('-').map(Number);
  return `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** cycleKey 'YYYY-MM' (0-indexed) → HTML <input type="month"> waarde 'YYYY-MM' (1-indexed). */
export function cycleKeyToMonthInput(cycleKey) {
  if (!cycleKey) return '';
  const [y, m] = cycleKey.split('-').map(Number);
  return `${y}-${String(m + 1).padStart(2, '0')}`;
}

/**
 * Sinking fund voor een niet-maandelijks planned item: hoeveel zou je
 * tegen nu opzij moeten hebben, gegeven hoeveel cycli je effectief aan
 * 't sparen bent?
 *
 * Per item explicit opt-in via `savingsActive` (toggled vanuit de
 * item-rij). Zonder die opt-in toont sinkingFundFor saved = 0 — anders
 * zou de UI cijfers tonen die in de realiteit niet kloppen ("X/12
 * geprojecteerd" terwijl er nog niks effectief is opzijgezet).
 *
 *  - elapsed = curKey - max(latest begrote betaling, savingsStartedAtCycle)
 *  - saved   = min(elapsed, freqM) * (a / freqM), capped op item.a
 *  - overdue = er was een begrote betaling, en elapsed > freqM
 *  - neverPaid = geen enkele begrote betaling gevonden in history
 *  - savingsActive = of de toggle aan staat (UI gebruikt dit voor de label)
 */
export function sinkingFundFor(person, type, index, cycleKey = curKey()) {
  const arr  = state.S[person]?.[type] || [];
  const item = arr[index];
  if (!item) return { saved: 0, total: 0, pct: 0, overdue: false, neverPaid: true, savingsActive: false };

  const total  = item.a;
  const months = freqM(item);
  const share  = total / months;

  let latestKey = null;
  const want = `${type}:${index}`;
  Object.entries(state.S[person]?.hist || {}).forEach(([k, h]) => {
    (h.ex || []).forEach(e => {
      if (e.vrKey === want && e.begroot) {
        if (!latestKey || k > latestKey) latestKey = k;
      }
    });
  });

  const savingsActive = !!item.savingsActive;

  // Niet actief aan het sparen → toon geen projectie. Dat is het hele
  // punt van de opt-in: misleidende "X/Y gespaard"-cijfers vermijden.
  if (!savingsActive) {
    return {
      saved: 0,
      total,
      pct: 0,
      elapsed: 0,
      months,
      overdue: false,
      neverPaid: !latestKey,
      savingsActive: false
    };
  }

  // Actief sparen: ref = latere van (laatste begrote betaling, expliciet
  // gezet start-moment). Zonder beide val terug op cycleKey = 0 elapsed.
  const curIdx = cycleIndex(cycleKey);
  const startKey = item.savingsStartedAtCycle || cycleKey;
  const refKey = (latestKey && latestKey > startKey) ? latestKey : startKey;
  const elapsed = Math.max(0, curIdx - cycleIndex(refKey));
  const saved   = Math.min(elapsed, months) * share;
  const pct     = total > 0 ? saved / total * 100 : 0;
  const overdue = !!latestKey && elapsed > months;
  const neverPaid = !latestKey;

  return { saved, total, pct, elapsed, months, overdue, neverPaid, savingsActive: true };
}

/**
 * Welke cats van een persoon zijn deze cyclus over hun maandbudget?
 * Returns array van { cat, spent, budget }.
 */
export function catsOverMaandbudget(person, cycleKey = curKey()) {
  const wb = state.S[person]?.weekBudgets || {};
  const out = [];
  Object.keys(wb).forEach(catId => {
    const budget = monthBudgetFor(person, catId);
    if (budget <= 0) return;
    const spent = spentInCycle(person, catId, cycleKey);
    if (spent > budget) out.push({ catId, spent, budget });
  });
  return out;
}

/**
 * Planned-budget per cat = som van vast+var items in die cat (maandelijks).
 * Returns 0 als geen items in die cat.
 */
export function plannedBudgetForCat(person, catId) {
  const d = state.S[person] || {};
  let total = 0;
  (d.vast || []).forEach(i => { if (i.cat === catId) total += ma(i); });
  (d.var  || []).forEach(i => { if (i.cat === catId) total += ma(i); });
  return total;
}

/**
 * Welke cats zitten effectief boven hun planned-budget deze cyclus?
 * Cats met weekBudget worden hier overgeslagen — die hebben hun eigen
 * maandbudget-check via catsOverMaandbudget.
 *
 * Bug-fix 2026-06-22: de vorige versie vuurde bij élk bedrag (`spent > 0`),
 * waardoor zelfs een normale begrote vervoer-betaling van €100 op een
 * vervoer-plan van €100 als "boven plan" werd gemarkeerd. Nu enkel
 * wanneer de totale uitgaven écht boven het maandelijkse plan zitten.
 */
export function catsOverPlanned(person, cycleKey = curKey()) {
  const d = state.S[person] || {};
  const wb = d.weekBudgets || {};
  const seen = new Set();
  [...(d.vast || []), ...(d.var || [])].forEach(i => { if (i.cat) seen.add(i.cat); });

  const out = [];
  seen.forEach(catId => {
    if (wb[catId] > 0) return;   // overgelaten aan catsOverMaandbudget
    const planned = plannedBudgetForCat(person, catId);
    if (planned <= 0) return;
    const spent = spentInCycle(person, catId, cycleKey);
    if (spent > planned + 0.005) {
      out.push({ catId, spent, planned, over: spent - planned });
    }
  });
  return out;
}

// ---------- Surplus van vorige cyclus ----------

/** Cycle-key van de cyclus die direct vóór today's cycle valt. */
export function todayPrevCycleKey() {
  const [y, m] = todayKey().split('-').map(Number);
  let pm = m - 1, py = y;
  if (pm < 0) { pm = 11; py--; }
  return py + '-' + String(pm).padStart(2, '0');
}

/**
 * Toestand van de vorige cyclus-surplus voor een persoon:
 *   - { question: amt }  → nog niet afgehandeld + er was surplus
 *   - { handled, amount } → al afgehandeld (kind: 'spaar' | 'mee' | 'negeer')
 *   - null → geen surplus om te tonen
 */
export function surplusVorigeMaandState(person) {
  const pk = todayPrevCycleKey();
  const handled = state.S[person]?.surplusHandled?.[pk];
  if (handled) {
    return { handled: handled.kind || handled, amount: handled.amount || 0 };
  }
  // Skip als de vorige cyclus eindigde vóór de app-startdatum.
  // Dan was er geen tracking en is "resterend" louter aanname.
  const appStart = state.S.settings?.appStartDate;
  if (appStart) {
    const [, cEnd] = cycleRange(pk, ankerDay(person));
    if (cEnd < new Date(appStart)) return null;
  }
  const seg = homeSegs(person, pk);
  if (seg.resterend <= 0) return null;
  return { question: seg.resterend };
}

/**
 * Pie-segmenten voor de Inkomsten-tab van een persoon, gegroepeerd per
 * categorie. Telt vaste inkomsten (per cycle) + extra inkomsten uit hist.in.
 * Geen resterend-slice — dit toont enkel waar het inkomen vandaan komt.
 */
export function inkomstenSegs(person, cycleKey = curKey()) {
  const list   = inkomstenForCycle(person, cycleKey);
  const h      = histFor(person, cycleKey);
  const bucket = {};

  const bump = (catId, amt) => {
    if (!amt) return;
    bucket[catId] = (bucket[catId] || 0) + amt;
  };

  list.forEach(i => bump(i.cat, i.f === 'j' ? i.a / 12 : i.a));
  (h.in || []).forEach(r => bump(r.cat, r.a));

  const cats = Object.entries(bucket)
    .filter(([, a]) => a > 0)
    .map(([id, a]) => {
      const c = catById(id);
      return { id, label: c.label, emoji: c.emoji, color: c.color, amount: a };
    })
    .sort((x, y) => y.amount - x.amount);

  const totaal = cats.reduce((s, x) => s + x.amount, 0);
  return { cats, totaal };
}

/**
 * Pie-segmenten voor het Samen-overzicht. Gegroepeerd per categorie
 * over alle gezamenlijke vast + var items. Samen heeft geen inkomen-
 * concept, dus geen resterend-slice.
 */
export function samenSegs() {
  const s = state.S.samen || {};
  const h = histFor('samen');
  const bucket = {};

  const bump = (catId, amt) => {
    if (!amt) return;
    bucket[catId] = (bucket[catId] || 0) + amt;
  };

  (s.vast || []).forEach(i => bump(i.cat, ma(i)));
  (s.var  || []).forEach(i => bump(i.cat, ma(i)));
  (h.ex   || []).forEach(e => bump(e.cat, e.a));

  const cats = Object.entries(bucket)
    .filter(([, a]) => a > 0)
    .map(([id, a]) => {
      const c = catById(id);
      return { id, label: c.label, emoji: c.emoji, color: c.color, amount: a };
    })
    .sort((x, y) => y.amount - x.amount);

  const totaal = cats.reduce((s, x) => s + x.amount, 0);
  return { cats, totaal };
}

// ---------- Cashflow-prognose (Fase C) ----------

/**
 * Projecteer het rekeningsaldo `days` dagen vooruit op basis van geplande
 * maandelijkse inkomsten en vast/var uitgaven. Yearly items doen niet mee —
 * die horen op de spaarpot via sinking funds, niet op de lopende rekening.
 *
 * Retourneert null als `huidigSaldo` nog niet is ingesteld (caller toont
 * dan een setup-hint).
 *
 * TODO Fase C v2: yearly items met startMaand mee laten tellen.
 */
export function cashflowProjectie(person, days = 60) {
  if (person === 'samen') return null;
  const d = state.S[person];
  if (!d || d.huidigSaldo === null || d.huidigSaldo === undefined) return null;

  const ank = ankerDay(person);
  const today = state.TODAY;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end   = new Date(start);
  end.setDate(end.getDate() + days);

  const inkomstenM = (d.inkomsten || []).filter(i => i.f === 'm' && !i.freqM);
  const vastM = (d.vast || []).filter(i => (i.freqM ?? freqM(i)) === 1);
  const varM  = (d.var  || []).filter(i => (i.freqM ?? freqM(i)) === 1);

  // Gezamenlijke kosten worden in de praktijk altijd doorgestort naar de
  // samen-rekening — geld verlaat dus de persoonlijke rekening. Vroeger
  // sloegen we 't over als gemHandled nog niet getriggerd was; dat klopt
  // niet voor toekomstige cycli die in deze projectie vallen.

  // End-of-month clamp: betaaldag 31 → laatste dag van die maand.
  const matches = (dayInMonth, target, daysInMonth) => {
    if (target >= daysInMonth) return dayInMonth === daysInMonth;
    return dayInMonth === target;
  };

  let balance = d.huidigSaldo;
  let minBalance = balance;
  let minDate = new Date(start);
  const dailyBalances = [];

  const cur = new Date(start);
  while (cur <= end) {
    const dom = cur.getDate();
    const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
    const events = [];

    inkomstenM.forEach(inc => {
      const target = inc.startDag || 1;
      if (matches(dom, target, daysInMonth)) {
        balance += inc.a;
        events.push({ n: inc.n, a: inc.a, type: 'in' });
      }
    });

    vastM.forEach(it => {
      const target = it.betaalDag || ank;
      if (matches(dom, target, daysInMonth)) {
        const amt = ma(it);
        balance -= amt;
        events.push({ n: it.n, a: amt, type: 'uit' });
      }
    });

    varM.forEach(it => {
      const target = it.betaalDag || ank;
      if (matches(dom, target, daysInMonth)) {
        const amt = ma(it);
        balance -= amt;
        events.push({ n: it.n, a: amt, type: 'uit' });
      }
    });

    if (balance < minBalance) {
      minBalance = balance;
      minDate = new Date(cur);
    }
    dailyBalances.push({ date: new Date(cur), balance, events });
    cur.setDate(cur.getDate() + 1);
  }

  return {
    startSaldo: d.huidigSaldo,
    dailyBalances,
    minBalance,
    minDate,
    goesNegative: minBalance < 0
  };
}

/**
 * Schatting van wat er nu nog op de rekening zou moeten staan op basis van
 * wat in Florijn ingegeven is voor de huidige cyclus.
 *
 *   inkomsten al binnengekomen
 *   − vast/var-items waarvan betaaldag al gepasseerd is
 *   − alle hist.ex deze cyclus (reeds ingevoerde uitgaven)
 *   + hist.in deze cyclus (extra inkomsten)
 *
 * Dit is een vertrekpunt — bevat geen oude buffer van vorige cycli, en
 * mist alles wat de gebruiker nog niet invoerde (cash, kleine dingen).
 * De bankapp blijft de waarheid.
 */
export function voorgesteldSaldo(person) {
  if (person === 'samen') return null;
  const d = state.S[person];
  if (!d) return null;

  const ank = ankerDay(person);
  const today = state.TODAY;
  const dayOfMonth = today.getDate();

  // Anker-cyclus: events met target >= ank vielen in vorige kalendermaand
  // (en zijn dus al gepasseerd zodra we ergens na de anker zitten); events
  // met target < ank vallen in deze kalendermaand en zijn gepasseerd als
  // target <= today.
  const passedInCycle = (target) => {
    if (ank <= 1) return target <= dayOfMonth;
    if (target >= ank) return true;
    return target <= dayOfMonth;
  };

  let inIn = 0;
  (d.inkomsten || []).forEach(inc => {
    if ((inc.freqM ?? freqM(inc)) !== 1) return;
    const tgt = inc.startDag || 1;
    if (passedInCycle(tgt)) inIn += inc.a;
  });

  let inOut = 0;
  (d.vast || []).filter(i => (i.freqM ?? freqM(i)) === 1).forEach(it => {
    const tgt = it.betaalDag || ank;
    if (passedInCycle(tgt)) inOut += ma(it);
  });
  (d.var || []).filter(i => (i.freqM ?? freqM(i)) === 1).forEach(it => {
    const tgt = it.betaalDag || ank;
    if (passedInCycle(tgt)) inOut += ma(it);
  });

  const cKey = todayKey(person);
  const h = histFor(person, cKey);
  (h.in || []).forEach(r => { inIn += r.a || 0; });
  (h.ex || []).forEach(e => { inOut += e.a || 0; });

  return Math.round(inIn - inOut);
}

// ---------- Pensioensparen (Fase D — BE) ----------

/**
 * Status van het BE pensioensparen voor een persoon in een gegeven jaar.
 * Telt alle hist.ex entries met cat='pensioen' over alle cycli van dat jaar.
 *
 * Fiscaal voordeel BE:
 *   - €1.050 grens → 30% belastingvermindering
 *   - €1.350 grens → 25% belastingvermindering (verhoogd plafond)
 *
 *  gestort   som van pensioen-uitgaven dit jaar
 *  grens     fiscale grens (persoonlijke instelling)
 *  rest      grens − gestort (negatief = boven grens, geen extra voordeel)
 *  voordeel  belastingvermindering (gestort × voordeel%, gecapt op grens)
 *  pct       ratio gestort / grens
 */
export function pensioensparenStatus(person, year) {
  const d = state.S[person] || {};
  const grens = d.pensioenGrens || 1050;
  const voordeelPct = grens >= 1350 ? 0.25 : 0.30;
  const yr = year || state.TODAY.getFullYear();

  let gestort = 0;
  Object.entries(d.hist || {}).forEach(([cKey, h]) => {
    // cycleKey 'YYYY-MM' — match op jaar-prefix.
    if (!cKey.startsWith(String(yr))) return;
    (h.ex || []).forEach(e => {
      if (e.cat === 'pensioen') gestort += e.a || 0;
    });
  });

  const rest = Math.max(0, grens - gestort);
  const benutBasis = Math.min(gestort, grens);
  const voordeel = benutBasis * voordeelPct;
  const pct = grens > 0 ? Math.min(100, (gestort / grens) * 100) : 0;

  return { gestort, grens, rest, voordeel, voordeelPct, pct, year: yr, overGrens: gestort > grens };
}

// ---------- Coaching (Fase E) ----------

/**
 * Spaarquote dit jaar: som van sparen-uitgaven over alle cycli in `year`,
 * gedeeld door 12× vast loon (= benadering van jaarinkomen netto, want
 * Florijn rekent op vast loon). Returns { spaar, basis, pct }.
 *
 * Vast loon is een momentopname — bij loonsverhoging in de loop van het
 * jaar is dit een ruwe benadering, maar volstaat voor de coaching-regel.
 */
export function spaarquoteJaar(person, year) {
  const d = state.S[person] || {};
  const yr = year || state.TODAY.getFullYear();

  let spaar = 0;
  Object.entries(d.hist || {}).forEach(([cKey, h]) => {
    if (!cKey.startsWith(String(yr))) return;
    (h.ex || []).forEach(e => {
      if (e.cat === 'sparen' || e.cat === 'extra_sparen') spaar += e.a || 0;
    });
  });

  const loon = vastLoon(person);
  const basis = loon * 12;
  const pct = basis > 0 ? (spaar / basis) * 100 : 0;
  return { spaar, basis, pct, year: yr };
}

/**
 * Cycle-key van de cyclus die direct vóór today's cycle viel — alléén
 * wanneer die nog niet "klaar verklaard" is in cycleReviews én eindigde
 * ná de appStartDate. Anders null (= geen reflectie-notif).
 */
export function reflectiePendingCycleKey(person) {
  const pk = todayPrevCycleKey();
  const d = state.S[person] || {};
  const reviewed = (d.cycleReviews || {})[pk];
  if (reviewed) return null;
  const appStart = state.S.settings?.appStartDate;
  if (appStart) {
    const [, cEnd] = cycleRange(pk, ankerDay(person));
    if (cEnd < new Date(appStart)) return null;
  }
  return pk;
}

/**
 * Pak één abonn-item dat aan een check-in toe is: nooit bevestigd OF
 * laatste bevestiging > 30 dagen geleden, EN niet "overgeslagen" voor
 * deze cyclus. Returns { idx, item } of null.
 *
 * Throttling: max één prompt per render (oudste bevestiging eerst), zodat
 * de notif-stack niet vol abonnementen-vragen komt te zitten.
 */
export function abonnOmTeBevragen(person) {
  if (person === 'samen') return null;
  const d = state.S[person] || {};
  const cKey = curKey();
  const today = state.TODAY;

  const eligible = (d.var || [])
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it.cat === 'abonn')
    .filter(({ it }) => it.abonnSkipCycle !== cKey)
    .map(({ it, idx }) => {
      const last = it.lastUsedConfirmedAt ? new Date(it.lastUsedConfirmedAt) : null;
      const days = last ? Math.floor((today - last) / 86400000) : Infinity;
      return { it, idx, days };
    })
    .filter(x => x.days > 30);

  if (!eligible.length) return null;
  eligible.sort((a, b) => b.days - a.days);
  return { idx: eligible[0].idx, item: eligible[0].it, days: eligible[0].days };
}

/**
 * Abonn-items voor de maandelijkse reflectie-modal: max 2 random,
 * eligible op dezelfde regel als `abonnOmTeBevragen` (laatst bevestigd
 * > 30 dagen geleden, deze cyclus niet expliciet "sla over").
 *
 * Twee modes:
 *   - ids = null  → kies 2 nieuwe random eligible (gebruikt door openReflect
 *                   om de selectie vast te leggen wanneer de modal opent).
 *   - ids = []    → geef de bestaande lockt-down picks terug, gefilterd op
 *                   nog-altijd-eligible (zo verdwijnen items uit de UI
 *                   zodra de gebruiker er een heeft beantwoord).
 *
 * Returned items hebben { idx, item, days }-vorm — identiek aan de
 * solo-versie zodat actions die idx krijgen blijven werken.
 */
export function abonnsForReflection(person, ids) {
  if (person === 'samen') return [];
  const d = state.S[person] || {};
  const cKey = curKey();
  const today = state.TODAY;

  const all = (d.var || [])
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it.cat === 'abonn');

  const eligible = (x) => {
    if (x.it.abonnSkipCycle === cKey) return false;
    const last = x.it.lastUsedConfirmedAt ? new Date(x.it.lastUsedConfirmedAt) : null;
    const days = last ? Math.floor((today - last) / 86400000) : Infinity;
    x.days = days;   // bewaren voor UI
    return days > 30;
  };

  if (Array.isArray(ids) && ids.length) {
    return ids
      .map(id => all.find(x => x.it.__id === id))
      .filter(x => x && eligible(x));
  }

  // Geen ids → verse pick: max 2 random eligible.
  const pool = all.filter(eligible);
  // Fisher-Yates light: shuffle + slice(0,2). Voor 2-5 abonnementen prima.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 2);
}
